import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { buildShareUrl } from "../../utils/shareUtils";
import { Eye, Heart, MessageCircle, ExternalLink, Plus, Lock, Pencil, ArrowLeft } from "lucide-react";

// ============= Post Reward Utilities (Phase 2) =============
function ymd(d = new Date()) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function addDaysStr(dateStr, delta) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return ymd(dt);
}

function getPostRewardState(keyPrefix = "wassla_post_reward") {
  try {
    const raw = localStorage.getItem(keyPrefix);
    return raw ? JSON.parse(raw) : { lastPostDate: null, streak: 0, total: 0 };
  } catch {
    return { lastPostDate: null, streak: 0, total: 0 };
  }
}

function setPostRewardState(state, keyPrefix = "wassla_post_reward") {
  try {
    localStorage.setItem(keyPrefix, JSON.stringify(state));
  } catch {}
}

// ============= CSS Keyframes (injected once) =============
const KEYFRAMES_ID = "pss-keyframes";
function injectKeyframes() {
  if (document.getElementById(KEYFRAMES_ID)) return;
  const style = document.createElement("style");
  style.id = KEYFRAMES_ID;
  style.textContent = `
    @keyframes pss-bounce {
      0%   { transform: scale(0.3); opacity: 0; }
      50%  { transform: scale(1.15); opacity: 1; }
      70%  { transform: scale(0.9); }
      100% { transform: scale(1); }
    }
    @keyframes pss-check {
      0%   { stroke-dashoffset: 30; }
      100% { stroke-dashoffset: 0; }
    }
    @keyframes pss-ring {
      0%   { transform: scale(0.8); opacity: 0; }
      50%  { opacity: 1; }
      100% { transform: scale(1.5); opacity: 0; }
    }
    @keyframes pss-particle {
      0%   { transform: translate(0,0) scale(1); opacity: 1; }
      100% { opacity: 0; }
    }
    @keyframes pss-fadeUp {
      0%   { transform: translateY(12px); opacity: 0; }
      100% { transform: translateY(0); opacity: 1; }
    }
    @keyframes pss-countUp {
      0%   { transform: translateY(6px); opacity: 0; }
      100% { transform: translateY(0); opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}

// ============= Main Component =============
export default function PublishSuccessScreen({ pendingReview = false }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("ar") ? "ar" : "en";
  const isRTL = locale === "ar";

  const adId = params.get("adId") || "";
  const hub = params.get("hub") || "main";

  const adUrl = useMemo(() => buildShareUrl(`/ad/${adId}`), [adId]);

  const [copied, setCopied] = useState(false);

  // Inject keyframes on mount
  useEffect(() => { injectKeyframes(); }, []);

  // Phase 2: Post Reward State
  const ENABLE_REWARD = process.env.REACT_APP_ENABLE_POST_REWARD === "true";
  const [reward, setReward] = useState(null);

  useEffect(() => {
    if (!ENABLE_REWARD || !adId) return;
    const key = "wassla_post_reward";
    const today = ymd();
    const s = getPostRewardState(key);
    let streak = s.streak || 0;
    if (!s.lastPostDate) {
      streak = 1;
    } else if (s.lastPostDate === today) {
      streak = Math.max(1, streak);
    } else if (addDaysStr(s.lastPostDate, 1) === today) {
      streak = streak + 1;
    } else {
      streak = 1;
    }
    const total = (s.total || 0) + (s.lastPostDate === today ? 0 : 1);
    const next = { lastPostDate: today, streak, total };
    setPostRewardState(next, key);
    setReward(next);
  }, [ENABLE_REWARD, adId]);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(adUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      const el = document.createElement("textarea");
      el.value = adUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  // Enhanced WhatsApp share message
  const onWhatsAppShare = () => {
    const adTitle = params.get("title") || "";
    const text =
      locale === "ar"
        ? `شاهد هذا الإعلان على وصلة:\n${adTitle ? adTitle + "\n" : ""}${adUrl}\n\nيمكنك التواصل معي عبر التطبيق.`
        : `Check out this ad on Wassla:\n${adTitle ? adTitle + "\n" : ""}${adUrl}\n\nYou can contact me through the app.`;
    const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(wa, "_blank", "noopener,noreferrer");
  };

  const backLabel = hub === "ladies" ? t("ps.backLadies") : t("ps.backHome");
  const backRoute = hub === "ladies" ? "/hubs/ladies" : "/";
  const postAnotherRoute = hub === "ladies" ? "/create-ad?hub=ladies" : "/create-ad";

  const font = isRTL ? "'Cairo', sans-serif" : "'Poppins', sans-serif";

  // Confetti particles (8 small dots around the checkmark)
  const particles = useMemo(() => {
    return Array.from({ length: 8 }).map((_, i) => {
      const angle = (i / 8) * 360;
      const rad = (angle * Math.PI) / 180;
      const dist = 38 + Math.random() * 14;
      const x = Math.cos(rad) * dist;
      const y = Math.sin(rad) * dist;
      const colors = ["#1F6F3A", "#25D366", "#F59E0B", "#3B82F6", "#EF4444", "#8B5CF6"];
      return { x, y, color: colors[i % colors.length], delay: i * 0.05, size: 4 + Math.random() * 3 };
    });
  }, []);

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      data-testid="publish-success-screen"
      style={{
        minHeight: "calc(100vh - 72px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        paddingBottom: 90,
        background: "linear-gradient(180deg, rgba(46,125,92,0.06) 0%, rgba(255,255,255,1) 60%)",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 480,
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
          padding: 20,
          border: "1px solid rgba(0,0,0,0.05)",
        }}
      >
        {/* ── Success Badge with bounce animation + confetti ── */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 10, position: "relative" }}>
          {/* Expanding ring */}
          <div
            style={{
              position: "absolute",
              width: 56,
              height: 56,
              borderRadius: 999,
              border: "2px solid rgba(46,125,92,0.25)",
              animation: "pss-ring 1s ease-out forwards",
            }}
          />
          {/* Confetti particles */}
          {particles.map((p, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                width: p.size,
                height: p.size,
                borderRadius: 999,
                background: p.color,
                top: "50%",
                left: "50%",
                transform: `translate(${p.x}px, ${p.y}px) scale(1)`,
                animation: `pss-particle 0.8s ${0.3 + p.delay}s ease-out forwards`,
                opacity: 0,
                animationFillMode: "both",
              }}
            />
          ))}
          {/* Checkmark circle */}
          <div
            aria-hidden="true"
            data-testid="success-badge"
            style={{
              width: 56,
              height: 56,
              borderRadius: 999,
              display: "grid",
              placeItems: "center",
              background: "rgba(46,125,92,0.12)",
              border: "2px solid rgba(46,125,92,0.25)",
              animation: "pss-bounce 0.6s ease-out",
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M20 6L9 17L4 12"
                stroke="#1F6F3A"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  strokeDasharray: 30,
                  strokeDashoffset: 0,
                  animation: "pss-check 0.4s 0.3s ease-out backwards",
                }}
              />
            </svg>
          </div>
        </div>

        {/* ── Title ── */}
        <h1
          data-testid="success-title"
          style={{
            margin: 0,
            textAlign: "center",
            fontSize: 20,
            fontWeight: 800,
            fontFamily: font,
            animation: "pss-fadeUp 0.5s 0.2s ease-out backwards",
          }}
        >
          {t("ps.title")}
        </h1>

        <p
          data-testid="success-subtitle"
          style={{
            margin: "6px 0 0",
            textAlign: "center",
            fontSize: 14,
            color: "rgba(0,0,0,0.65)",
            fontFamily: font,
            animation: "pss-fadeUp 0.5s 0.3s ease-out backwards",
          }}
        >
          {t("ps.subtitle")}
        </p>

        {/* ── Enhancement 1: Live message lines ── */}
        <div
          data-testid="live-message"
          style={{
            marginTop: 8,
            textAlign: "center",
            animation: "pss-fadeUp 0.5s 0.4s ease-out backwards",
          }}
        >
          <p style={{ margin: 0, fontSize: 13, color: "rgba(0,0,0,0.55)", fontFamily: font, lineHeight: 1.6 }}>
            {t("ps.liveMsg1")}
          </p>
          <p style={{ margin: 0, fontSize: 13, color: "rgba(0,0,0,0.55)", fontFamily: font, lineHeight: 1.6 }}>
            {t("ps.liveMsg2")}
          </p>
        </div>

        {/* ── Enhancement 2: Stats row ── */}
        <div
          data-testid="stats-row"
          style={{
            marginTop: 14,
            display: "flex",
            justifyContent: "center",
            gap: 0,
            animation: "pss-fadeUp 0.5s 0.5s ease-out backwards",
          }}
        >
          {[
            { icon: <Eye size={15} strokeWidth={2.2} />, label: t("ps.statsViews"), value: 0, color: "#6B7280" },
            { icon: <Heart size={15} strokeWidth={2.2} />, label: t("ps.statsInterested"), value: 0, color: "#EF4444" },
            { icon: <MessageCircle size={15} strokeWidth={2.2} />, label: t("ps.statsMessages"), value: 0, color: "#3B82F6" },
          ].map((stat, i) => (
            <div
              key={i}
              data-testid={`stat-${stat.label.toLowerCase()}`}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                padding: "10px 4px",
                borderRight: i < 2 ? "1px solid rgba(0,0,0,0.06)" : "none",
                borderLeft: isRTL && i < 2 ? "1px solid rgba(0,0,0,0.06)" : isRTL ? "none" : undefined,
                animation: `pss-countUp 0.4s ${0.6 + i * 0.1}s ease-out backwards`,
              }}
            >
              <span style={{ color: stat.color, display: "flex", alignItems: "center" }}>{stat.icon}</span>
              <span style={{ fontSize: 18, fontWeight: 800, color: "#111", fontFamily: font }}>{stat.value}</span>
              <span style={{ fontSize: 11, color: "rgba(0,0,0,0.45)", fontFamily: font }}>{stat.label}</span>
            </div>
          ))}
        </div>

        {/* Phase 2: Progress Reward Pill */}
        {ENABLE_REWARD && reward && (
          <div
            data-testid="reward-pill"
            style={{
              marginTop: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <div
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                background: "rgba(46,125,92,0.10)",
                border: "1px solid rgba(46,125,92,0.18)",
                fontWeight: 700,
                fontSize: 11,
                color: "#1F6F3A",
                fontFamily: font,
              }}
            >
              {t("ps.rewardStreak", { streak: reward.streak })}
            </div>
            <span style={{ fontSize: 11, color: "rgba(0,0,0,0.5)" }}>
              {t("ps.keepPosting")}
            </span>
          </div>
        )}

        {/* Optional pending review banner */}
        {pendingReview && (
          <div
            data-testid="pending-review-banner"
            style={{
              marginTop: 14,
              borderRadius: 14,
              padding: "12px 14px",
              background: "rgba(245, 158, 11, 0.10)",
              border: "1px solid rgba(245, 158, 11, 0.25)",
            }}
          >
            <div style={{ fontWeight: 800, marginBottom: 4, color: "#92400e" }}>
              {t("ps.pendingTitle")}
            </div>
            <div style={{ color: "rgba(0,0,0,0.72)", fontSize: 14 }}>
              {t("ps.pendingBody")}
            </div>
          </div>
        )}

        {/* Tip checklist */}
        <div
          data-testid="success-checklist"
          style={{
            marginTop: 12,
            borderRadius: 10,
            padding: "10px 12px",
            background: "rgba(46,125,92,0.05)",
            border: "1px solid rgba(46,125,92,0.12)",
          }}
        >
          <div style={{
            fontWeight: 700,
            fontSize: 12,
            color: "#1F6F3A",
            marginBottom: 6,
            textTransform: "uppercase",
            letterSpacing: "0.03em",
            fontFamily: font,
          }}>
            {t("ps.sellFaster")}
          </div>
          <ul style={{
            margin: 0,
            padding: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}>
            {["tipWhatsApp", "tipPhotos", "tipDescription"].map((key) => (
              <li key={key} style={{ fontSize: 12, color: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ color: "#1F6F3A", fontWeight: 700 }}>•</span> {t(`ps.${key}`)}
              </li>
            ))}
          </ul>
        </div>

        {/* ── CTA Buttons ── */}
        <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
          {/* Enhancement 3: Primary CTA — View Ad with eye icon */}
          <button
            onClick={() => navigate(`/ad/${adId}`)}
            data-testid="view-ad-btn"
            style={{
              height: 46,
              borderRadius: 12,
              border: "none",
              background: "#1F6F3A",
              color: "#fff",
              fontWeight: 700,
              fontSize: 15,
              cursor: "pointer",
              transition: "transform 0.15s, box-shadow 0.15s",
              boxShadow: "0 4px 12px rgba(46,125,92,0.25)",
              fontFamily: font,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = "0 6px 16px rgba(46,125,92,0.35)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(46,125,92,0.25)";
            }}
          >
            <Eye size={18} strokeWidth={2.5} />
            {t("ps.viewAd")}
          </button>

          {/* Enhancement 5: Secondary — Post new ad with helper text */}
          <div style={{ textAlign: "center" }}>
            <button
              onClick={() => navigate(postAnotherRoute)}
              data-testid="post-another-btn"
              style={{
                width: "100%",
                height: 44,
                borderRadius: 12,
                border: "1px dashed rgba(46,125,92,0.35)",
                background: "rgba(46,125,92,0.05)",
                color: "#1F6F3A",
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
                transition: "background 0.15s",
                fontFamily: font,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
              onMouseOver={(e) => e.currentTarget.style.background = "rgba(46,125,92,0.10)"}
              onMouseOut={(e) => e.currentTarget.style.background = "rgba(46,125,92,0.05)"}
            >
              <Plus size={16} strokeWidth={2.5} />
              {t("ps.postAnother")}
            </button>
            <p
              data-testid="post-another-hint"
              style={{
                margin: "5px 0 0",
                fontSize: 11.5,
                color: "rgba(0,0,0,0.42)",
                fontFamily: font,
              }}
            >
              {t("ps.postAnotherHint")}
            </p>
          </div>

          {/* Row: Edit + Back */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <button
              onClick={() => navigate(`/edit-ad/${adId}`)}
              data-testid="edit-ad-btn"
              style={{
                height: 40,
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                color: "rgba(0,0,0,0.7)",
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
                transition: "background 0.15s",
                fontFamily: font,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 5,
              }}
              onMouseOver={(e) => e.currentTarget.style.background = "rgba(0,0,0,0.03)"}
              onMouseOut={(e) => e.currentTarget.style.background = "#fff"}
            >
              <Pencil size={13} />
              {t("ps.editAd")}
            </button>

            <button
              onClick={() => navigate(backRoute)}
              data-testid="back-btn"
              style={{
                height: 40,
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                color: "rgba(0,0,0,0.7)",
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
                transition: "background 0.15s",
                fontFamily: font,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 5,
              }}
              onMouseOver={(e) => e.currentTarget.style.background = "rgba(0,0,0,0.03)"}
              onMouseOut={(e) => e.currentTarget.style.background = "#fff"}
            >
              <ArrowLeft size={13} />
              {backLabel}
            </button>
          </div>
        </div>

        {/* ── Share Section ── */}
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid rgba(0,0,0,0.05)" }}>
          <div
            data-testid="share-title"
            style={{
              fontWeight: 700,
              fontSize: 13,
              marginBottom: 8,
              color: "rgba(0,0,0,0.75)",
              fontFamily: font,
            }}
          >
            {t("ps.shareTitle")}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {/* Enhancement 4: WhatsApp with improved message */}
            <button
              onClick={onWhatsAppShare}
              data-testid="whatsapp-share-btn"
              style={{
                height: 40,
                borderRadius: 10,
                border: "1px solid rgba(37,211,102,0.25)",
                background: "rgba(37,211,102,0.08)",
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                color: "#128C7E",
                fontSize: 12,
                transition: "background 0.15s",
                fontFamily: font,
              }}
              onMouseOver={(e) => e.currentTarget.style.background = "rgba(37,211,102,0.15)"}
              onMouseOut={(e) => e.currentTarget.style.background = "rgba(37,211,102,0.08)"}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              {t("ps.waShare")}
            </button>

            <button
              onClick={onCopy}
              data-testid="copy-link-btn"
              style={{
                height: 40,
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.10)",
                background: copied ? "rgba(46,125,92,0.1)" : "#fff",
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                color: copied ? "#1F6F3A" : "rgba(0,0,0,0.75)",
                fontSize: 12,
                transition: "all 0.15s",
                fontFamily: font,
              }}
            >
              {copied ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1F6F3A" strokeWidth="3">
                  <path d="M20 6L9 17L4 12" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                  <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                </svg>
              )}
              {copied ? t("ps.copied") : t("ps.copyLink")}
            </button>
          </div>

          {/* Enhancement 6: Trust / Safety message */}
          <div
            data-testid="safety-note"
            style={{
              marginTop: 14,
              fontSize: 12,
              color: "rgba(0,0,0,0.45)",
              display: "flex",
              alignItems: "center",
              gap: 6,
              justifyContent: "center",
              fontFamily: font,
            }}
          >
            <Lock size={13} strokeWidth={2.2} />
            {t("ps.safety")}
          </div>
        </div>
      </div>
    </div>
  );
}
